import { supabase } from '../supabase';

export interface TeacherClassSummary {
  id: string;
  name: string;
  /** Public URL from storage (optional) */
  class_image_url?: string | null;
  activeStudents: number;
  upcomingAssignments: number;
  recentSubmissions: number;
}

export interface TeacherClassDetails {
  id: string;
  name: string;
  description: string | null;
  class_image_url?: string | null;
}

export interface TeacherClassTextbook {
  id: string;
  class_id: string;
  title: string;
  file_path: string;
  file_url: string;
  page_count: number;
  uploaded_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface TeacherClassSyllabus {
  id: string;
  class_id: string;
  title: string;
  file_path: string;
  file_url: string;
  uploaded_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface TeacherLesson {
  id: string;
  class_id: string;
  title: string;
  lesson_date: string | null;
  description: string | null;
  sequence: number;
  archived?: boolean;
}

export interface TeacherWorkItem {
  id: string;
  class_id: string;
  title: string;
  type: 'quiz' | 'assignment';
  description?: string | null;
  due_at: string | null;
  published_at?: string | null;
  points: number;
  archived?: boolean;
}

export interface TeacherGradeRow {
  id: string;
  student_name: string;
  student_id?: string | null;
  /** Public profile image URL for the student (optional) */
  student_avatar_url?: string | null;
  work_title: string;
  /** Assignment/quiz id from submissions.work_id when available */
  work_id?: string | null;
  status: 'submitted' | 'missing' | 'graded' | 'returned';
  score: number | null;
  feedback: string | null;
  /** When present, ties the row to a class (from submissions.class_id) */
  class_id?: string | null;
  /** Optional metadata for the grading queue UI */
  class_name?: string;
  assignment_category?: string | null;
  submitted_at?: string | null;
  queue_badge?: 'ready' | 'resubmitted' | 'late';
  work_type?: 'quiz' | 'assignment';
  max_points?: number | null;
}

export interface TeacherGradeSubmissionDetail {
  submission: TeacherGradeRow;
  answers_by_question_id: Record<string, string>;
}

export interface TeacherAnnouncement {
  id: string;
  class_id: string | null;
  title: string;
  body: string;
  created_at: string;
}

export interface TeacherMessage {
  id: string;
  sender_id: string;
  receiver_id: string;
  content: string;
  created_at: string;
}

export interface TeacherMessageContact {
  id: string;
  full_name: string;
  email: string | null;
  avatar_url?: string | null;
}

export interface TeacherBroadcastClass {
  id: string;
  name: string;
  studentCount: number;
}

export interface ClassroomBroadcastResult {
  ok: boolean;
  sent_count?: number;
  skipped_count?: number;
  class_id?: string;
  error?: string;
}

export type DiscussionPostStatus = 'published' | 'removed';

export interface DiscussionReactionSummary {
  emoji: string;
  count: number;
  reactedByMe: boolean;
}

export interface TeacherDiscussionPost {
  id: string;
  class_id: string;
  parent_post_id: string | null;
  author_id: string | null;
  author_name: string;
  body: string;
  status: DiscussionPostStatus;
  is_pinned: boolean;
  hidden_at: string | null;
  hidden_by: string | null;
  created_at: string;
  updated_at: string;
  reactions: DiscussionReactionSummary[];
  replies: TeacherDiscussionPost[];
}

export interface TeacherDiscussionSettings {
  class_id: string;
  is_locked: boolean;
  updated_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface TeacherClassDiscussion {
  settings: TeacherDiscussionSettings;
  posts: TeacherDiscussionPost[];
}

export interface ClassroomStudentOption {
  id: string;
  full_name: string;
  email: string | null;
  /** Public profile image URL from `users.avatar` when available */
  avatar_url?: string | null;
}

export interface TeacherActivitySummary {
  lessonsCompleted: number;
  lessonsMissed: number;
  assignmentsCompleted: number;
  assignmentsMissed: number;
  totalLessons: number;
  totalExpectedLessonCompletions: number;
}

const sampleAnnouncements: TeacherAnnouncement[] = [
  {
    id: 'announcement-1',
    class_id: 'class-1',
    title: 'Reminder',
    body: 'Submit your reflection journal before Friday.',
    created_at: new Date().toISOString(),
  },
];

const sampleMessages: TeacherMessage[] = [
  {
    id: 'message-1',
    sender_id: 'student-1',
    receiver_id: 'teacher-1',
    content: 'Can I get clarification on Assignment 2?',
    created_at: new Date().toISOString(),
  },
];

const fallbackOr = <T>(rows: T[] | null, fallback: T[]) => (rows && rows.length > 0 ? rows : fallback);
const RECENT_SUBMISSION_WINDOW_MS = 1000 * 60 * 60 * 24 * 7;
const TEXTBOOK_ASSET_BUCKET = 'lesson-assets';

type LooseRow = Record<string, unknown>;

function pickString(row: LooseRow, keys: string[]): string | null {
  for (const key of keys) {
    const value = row[key];
    if (typeof value === 'string' && value.trim()) return value.trim();
  }
  return null;
}

function pickNumber(row: LooseRow, keys: string[]): number | null {
  for (const key of keys) {
    const value = row[key];
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    if (typeof value === 'string' && value.trim()) {
      const parsed = Number(value);
      if (Number.isFinite(parsed)) return parsed;
    }
  }
  return null;
}

function sanitizeFileName(name: string): string {
  return name.replace(/[^a-zA-Z0-9._-]/g, '_');
}

function isFutureIso(value: string | null): boolean {
  if (!value) return false;
  const when = new Date(value).getTime();
  if (Number.isNaN(when)) return false;
  return when >= Date.now();
}

function isRecentIso(value: string | null): boolean {
  if (!value) return true;
  const when = new Date(value).getTime();
  if (Number.isNaN(when)) return true;
  return Date.now() - when <= RECENT_SUBMISSION_WINDOW_MS;
}

function mapSubmissionRows(rows: LooseRow[]): TeacherGradeRow[] {
  return rows.map((row, index) => {
    const statusRaw = pickString(row, ['status'])?.toLowerCase() ?? 'submitted';
    const status: TeacherGradeRow['status'] =
      statusRaw === 'draft'
        ? 'missing'
        : statusRaw === 'missing' || statusRaw === 'graded' || statusRaw === 'returned' || statusRaw === 'submitted'
          ? statusRaw
          : 'submitted';
    const queue_badge: TeacherGradeRow['queue_badge'] =
      status === 'missing' ? 'late' : status === 'submitted' ? 'ready' : undefined;

    const id = pickString(row, ['id']) ?? `submission-${index + 1}`;
    const studentName = pickString(row, ['student_name', 'student_full_name', 'full_name']) ?? `Student ${index + 1}`;
    const studentId = pickString(row, ['student_id', 'studentId', 'user_id', 'userId']);
    const studentAvatarUrl = pickString(row, ['student_avatar_url', 'student_avatar', 'avatar_url', 'avatar']);
    const className = pickString(row, ['class_name']) ?? 'Class';
    const classIdRow = pickString(row, ['class_id', 'classId']);
    const workId = pickString(row, ['work_id', 'workId', 'assignment_id', 'quiz_id']);
    const workTitle = pickString(row, ['work_title', 'assignment_title', 'title']) ?? 'Assignment';
    const assignmentCategory = pickString(row, ['assignment_category', 'category', 'module']);
    const submittedAt = pickString(row, ['submitted_at', 'created_at', 'updated_at']);
    const workTypeRaw = pickString(row, ['work_type', 'type'])?.toLowerCase();
    const workType: TeacherGradeRow['work_type'] =
      workTypeRaw === 'quiz' || workTypeRaw === 'assignment' ? workTypeRaw : 'assignment';

    return {
      id,
      student_name: studentName,
      student_id: studentId,
      student_avatar_url: studentAvatarUrl,
      class_id: classIdRow,
      class_name: className,
      work_id: workId,
      work_title: workTitle,
      assignment_category: assignmentCategory,
      submitted_at: submittedAt,
      work_type: workType,
      max_points: pickNumber(row, ['max_points', 'points']),
      queue_badge,
      status,
      score: pickNumber(row, ['score']),
      feedback: pickString(row, ['feedback']),
    };
  });
}

function statusPriority(status: TeacherGradeRow['status']): number {
  if (status === 'graded' || status === 'returned') return 3;
  if (status === 'submitted') return 2;
  return 1;
}

function rowTimestamp(row: TeacherGradeRow): number {
  if (!row.submitted_at) return 0;
  const ts = new Date(row.submitted_at).getTime();
  return Number.isNaN(ts) ? 0 : ts;
}

function dedupeSubmissionRows(rows: TeacherGradeRow[]): TeacherGradeRow[] {
  const latestByStudent = new Map<string, TeacherGradeRow>();
  const passthrough: TeacherGradeRow[] = [];

  rows.forEach((row) => {
    const studentIdKey = row.student_id?.trim() ? `id:${row.student_id.trim()}` : '';
    const studentNameKey = row.student_name.trim() ? `name:${row.student_name.trim().toLowerCase()}` : '';
    const key = studentIdKey || studentNameKey;
    if (!key) {
      passthrough.push(row);
      return;
    }
    const prev = latestByStudent.get(key);
    if (!prev) {
      latestByStudent.set(key, row);
      return;
    }

    const prevIsMissing = prev.status === 'missing';
    const currIsMissing = row.status === 'missing';
    if (prevIsMissing !== currIsMissing) {
      latestByStudent.set(key, currIsMissing ? prev : row);
      return;
    }

    const prevTs = rowTimestamp(prev);
    const currTs = rowTimestamp(row);
    if (currTs > prevTs) {
      latestByStudent.set(key, row);
      return;
    }
    if (currTs === prevTs && statusPriority(row.status) > statusPriority(prev.status)) {
      latestByStudent.set(key, row);
    }
  });

  return Array.from(latestByStudent)
    .map(([, row]) => row)
    .concat(passthrough)
    .sort((a, b) => rowTimestamp(b) - rowTimestamp(a));
}

function isPlaceholderStudentName(name: string): boolean {
  return /^student\s+\d+$/i.test(name.trim());
}

function toAnswerMap(value: unknown): Record<string, string> | null {
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return {};
    try {
      return toAnswerMap(JSON.parse(trimmed));
    } catch {
      return null;
    }
  }
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const out: Record<string, string> = {};
  Object.entries(value as Record<string, unknown>).forEach(([key, raw]) => {
    if (!key.trim()) return;
    if (typeof raw === 'string') {
      out[key] = raw;
      return;
    }
    if (typeof raw === 'number' || typeof raw === 'boolean') {
      out[key] = String(raw);
    }
  });
  return out;
}

function pickAnswerMap(row: LooseRow): Record<string, string> {
  const directCandidates = [
    'answers_by_question_id',
    'answers',
    'responses',
    'response_map',
    'answer_map',
    'submission_answers',
  ];
  for (const key of directCandidates) {
    const parsed = toAnswerMap(row[key]);
    if (parsed && Object.keys(parsed).length > 0) return parsed;
  }

  const nestedCandidates = ['payload', 'submission_payload', 'metadata', 'data'];
  for (const containerKey of nestedCandidates) {
    const container = row[containerKey];
    if (!container || typeof container !== 'object' || Array.isArray(container)) continue;
    const nested = container as Record<string, unknown>;
    for (const key of directCandidates) {
      const parsed = toAnswerMap(nested[key]);
      if (parsed && Object.keys(parsed).length > 0) return parsed;
    }
  }

  return {};
}

function toDiscussionStatus(value: unknown): DiscussionPostStatus {
  return value === 'removed' ? 'removed' : 'published';
}

function toSafeIso(value: unknown): string {
  if (typeof value === 'string' && value.trim()) return value;
  return new Date().toISOString();
}

const QUICK_DISCUSSION_REACTIONS = ['👍', '❤️', '😂', '😮', '😢', '🙏'] as const;

function sortDiscussionReactions(reactions: DiscussionReactionSummary[]): DiscussionReactionSummary[] {
  return [...reactions].sort((left, right) => {
    if (left.reactedByMe !== right.reactedByMe) return left.reactedByMe ? -1 : 1;
    const leftOrder = QUICK_DISCUSSION_REACTIONS.indexOf(left.emoji as (typeof QUICK_DISCUSSION_REACTIONS)[number]);
    const rightOrder = QUICK_DISCUSSION_REACTIONS.indexOf(right.emoji as (typeof QUICK_DISCUSSION_REACTIONS)[number]);
    const leftRank = leftOrder >= 0 ? leftOrder : Number.MAX_SAFE_INTEGER;
    const rightRank = rightOrder >= 0 ? rightOrder : Number.MAX_SAFE_INTEGER;
    if (leftRank !== rightRank) return leftRank - rightRank;
    if (left.count !== right.count) return right.count - left.count;
    return left.emoji.localeCompare(right.emoji);
  });
}

function buildDiscussionReactionSummaries(
  rows: Array<{ emoji?: string | null; user_id?: string | null }>,
  viewerUserId: string | null
): DiscussionReactionSummary[] {
  const byEmoji = new Map<string, DiscussionReactionSummary>();
  rows.forEach((row) => {
    const emoji = String(row.emoji ?? '').trim();
    const userId = String(row.user_id ?? '').trim();
    if (!emoji || !userId) return;
    const existing = byEmoji.get(emoji);
    if (existing) {
      existing.count += 1;
      existing.reactedByMe = existing.reactedByMe || Boolean(viewerUserId && userId === viewerUserId);
      return;
    }
    byEmoji.set(emoji, {
      emoji,
      count: 1,
      reactedByMe: Boolean(viewerUserId && userId === viewerUserId),
    });
  });
  return sortDiscussionReactions(Array.from(byEmoji.values()));
}

async function fetchDiscussionReactionSummaries(
  postIds: string[],
  viewerUserId: string | null
): Promise<Map<string, DiscussionReactionSummary[]>> {
  const reactionsByPostId = new Map<string, DiscussionReactionSummary[]>();
  if (postIds.length === 0) return reactionsByPostId;

  const { data, error } = await supabase
    .from('class_discussion_post_reactions')
    .select('post_id,user_id,emoji')
    .in('post_id', postIds);
  if (error || !data) return reactionsByPostId;

  const rowsByPostId = new Map<string, Array<{ emoji?: string | null; user_id?: string | null }>>();
  (((data ?? []) as unknown[]) as Array<{ post_id?: string; user_id?: string | null; emoji?: string | null }>).forEach(
    (row) => {
      const postId = typeof row.post_id === 'string' ? row.post_id : '';
      if (!postId) return;
      const list = rowsByPostId.get(postId) ?? [];
      list.push({ emoji: row.emoji, user_id: row.user_id });
      rowsByPostId.set(postId, list);
    }
  );

  rowsByPostId.forEach((rows, postId) => {
    reactionsByPostId.set(postId, buildDiscussionReactionSummaries(rows, viewerUserId));
  });

  return reactionsByPostId;
}

function mapDiscussionTree(
  rows: LooseRow[],
  authorNameById: Map<string, string>,
  reactionsByPostId: Map<string, DiscussionReactionSummary[]> = new Map()
): TeacherDiscussionPost[] {
  const byId = new Map<string, TeacherDiscussionPost>();

  rows.forEach((row, index) => {
    const id = pickString(row, ['id']) ?? `discussion-post-${index + 1}`;
    const authorId = pickString(row, ['author_id', 'authorId']);
    const fallbackName = authorId ? `User ${authorId.slice(0, 8)}` : 'Unknown user';
    byId.set(id, {
      id,
      class_id: pickString(row, ['class_id', 'classId']) ?? '',
      parent_post_id: pickString(row, ['parent_post_id', 'parentPostId']),
      author_id: authorId,
      author_name: authorId ? authorNameById.get(authorId) ?? fallbackName : 'Unknown user',
      body: pickString(row, ['body']) ?? '',
      status: toDiscussionStatus(pickString(row, ['status']) ?? 'published'),
      is_pinned: Boolean(row.is_pinned),
      hidden_at: pickString(row, ['hidden_at']),
      hidden_by: pickString(row, ['hidden_by']),
      created_at: toSafeIso(row.created_at),
      updated_at: toSafeIso(row.updated_at),
      reactions: reactionsByPostId.get(id) ?? [],
      replies: [],
    });
  });

  const roots: TeacherDiscussionPost[] = [];

  byId.forEach((post) => {
    if (post.parent_post_id && byId.has(post.parent_post_id)) {
      byId.get(post.parent_post_id)!.replies.push(post);
      return;
    }
    roots.push(post);
  });

  const byNewest = (a: TeacherDiscussionPost, b: TeacherDiscussionPost) =>
    new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
  const byOldest = (a: TeacherDiscussionPost, b: TeacherDiscussionPost) =>
    new Date(a.created_at).getTime() - new Date(b.created_at).getTime();

  roots.sort((a, b) => {
    if (a.is_pinned !== b.is_pinned) return a.is_pinned ? -1 : 1;
    return byNewest(a, b);
  });

  byId.forEach((post) => {
    post.replies.sort(byOldest);
  });

  return roots;
}

async function enrichRowsWithStudentProfiles(rows: TeacherGradeRow[]): Promise<TeacherGradeRow[]> {
  const studentIds = Array.from(new Set(rows.map((row) => row.student_id?.trim() ?? '').filter(Boolean)));
  if (studentIds.length === 0) return rows;

  const profileById = new Map<string, { full_name: string | null; avatar: string | null }>();
  const chunkSize = 100;
  for (let i = 0; i < studentIds.length; i += chunkSize) {
    const slice = studentIds.slice(i, i + chunkSize);
    const { data } = await supabase.from('users').select('id,full_name,avatar').in('id', slice);
    (((data ?? []) as unknown[]) as LooseRow[]).forEach((row) => {
      const id = pickString(row, ['id']);
      if (!id) return;
      profileById.set(id, {
        full_name: pickString(row, ['full_name']),
        avatar: pickString(row, ['avatar', 'avatar_url', 'photo_url']),
      });
    });
  }

  return rows.map((row) => {
    const studentId = row.student_id?.trim();
    if (!studentId) return row;
    const profile = profileById.get(studentId);
    if (!profile) return row;

    const shouldReplaceName = !row.student_name.trim() || isPlaceholderStudentName(row.student_name);
    return {
      ...row,
      student_name: shouldReplaceName ? profile.full_name ?? row.student_name : row.student_name,
      student_avatar_url: row.student_avatar_url ?? profile.avatar ?? null,
    };
  });
}

export const teacherService = {
  textbookAssetBucket: TEXTBOOK_ASSET_BUCKET,

  buildClassTextbookStoragePath(classId: string, originalName: string) {
    const classIdSafe = classId.trim();
    const safeName = sanitizeFileName(originalName || 'textbook.pdf');
    return `classroom/textbooks/${classIdSafe}/${Date.now()}_${Math.random().toString(36).slice(2)}_${safeName}`;
  },

  buildClassSyllabusStoragePath(classId: string, originalName: string) {
    const classIdSafe = classId.trim();
    const safeName = sanitizeFileName(originalName || 'syllabus.pdf');
    return `classroom/syllabi/${classIdSafe}/${Date.now()}_${Math.random().toString(36).slice(2)}_${safeName}`;
  },

  async getOverview(): Promise<TeacherClassSummary[]> {
    const { data: classRows, error: classError } = await supabase
      .from('classes')
      .select('id,name,class_image_url')
      .limit(200);
    if (classError) return [];

    const classes = (classRows ?? []) as Array<{ id: string; name: string; class_image_url?: string | null }>;
    if (classes.length === 0) return [];

    const classIds = classes.map((c) => c.id);
    const classIdSet = new Set(classIds);
    const nowIso = new Date().toISOString();
    const upcomingAssignmentsByClass = new Map<string, number>();
    const recentSubmissionsByClass = new Map<string, number>();
    const activeStudentsByClass = new Map<string, Set<string>>();

    const [assignmentsRes, quizzesRes, submissionsRes, enrollmentsRes] = await Promise.all([
      supabase
        .from('assignments')
        .select('class_id,due_at,archived')
        .in('class_id', classIds)
        .gte('due_at', nowIso),
      supabase
        .from('quizzes')
        .select('class_id,due_at,archived')
        .in('class_id', classIds)
        .gte('due_at', nowIso),
      supabase
        .from('submissions')
        .select('class_id,status,submitted_at,created_at,updated_at')
        .in('class_id', classIds),
      supabase
        .from('class_enrollments')
        .select('class_id,student_id')
        .in('class_id', classIds),
    ]);

    const scheduleRows: LooseRow[] = [
      ...(((assignmentsRes.data ?? []) as unknown[]) as LooseRow[]),
      ...(((quizzesRes.data ?? []) as unknown[]) as LooseRow[]),
    ];

    scheduleRows.forEach((row) => {
      const classId = pickString(row, ['class_id', 'classId']);
      if (!classId || !classIdSet.has(classId)) return;
      const archived = Boolean(row.archived);
      const dueAt = pickString(row, ['due_at', 'dueAt', 'due_date']);
      if (archived || !isFutureIso(dueAt)) return;
      upcomingAssignmentsByClass.set(classId, (upcomingAssignmentsByClass.get(classId) ?? 0) + 1);
    });

    (((enrollmentsRes.data ?? []) as unknown[]) as LooseRow[]).forEach((row) => {
      const classId = pickString(row, ['class_id', 'classId']);
      if (!classId || !classIdSet.has(classId)) return;
      const studentId = pickString(row, ['student_id', 'studentId']);
      if (!studentId) return;
      const set = activeStudentsByClass.get(classId) ?? new Set<string>();
      set.add(studentId);
      activeStudentsByClass.set(classId, set);
    });

    (((submissionsRes.data ?? []) as unknown[]) as LooseRow[]).forEach((row) => {
      const classId = pickString(row, ['class_id', 'classId']);
      if (!classId || !classIdSet.has(classId)) return;

      const statusRaw = pickString(row, ['status'])?.toLowerCase() ?? 'submitted';
      const status: TeacherGradeRow['status'] =
        statusRaw === 'missing' || statusRaw === 'graded' || statusRaw === 'returned' || statusRaw === 'submitted'
          ? statusRaw
          : 'submitted';
      const submittedAt = pickString(row, ['submitted_at', 'created_at', 'updated_at']);

      if (status === 'submitted' || status === 'graded' || status === 'returned') {
        if (isRecentIso(submittedAt)) {
          recentSubmissionsByClass.set(classId, (recentSubmissionsByClass.get(classId) ?? 0) + 1);
        }
      }
    });

    return classes.map((c) => ({
      id: c.id,
      name: c.name,
      class_image_url: c.class_image_url ?? null,
      activeStudents: activeStudentsByClass.get(c.id)?.size ?? 0,
      upcomingAssignments: upcomingAssignmentsByClass.get(c.id) ?? 0,
      recentSubmissions: recentSubmissionsByClass.get(c.id) ?? 0,
    }));
  },

  async getLessons(): Promise<TeacherLesson[]> {
    const query = async (withArchivedFilter: boolean) => {
      let builder = supabase
        .from('lessons')
        .select('id,class_id,title,lesson_date,description,sequence,archived')
        .order('lesson_date', { ascending: true });
      if (withArchivedFilter) {
        builder = builder.eq('archived', false);
      }
      return builder;
    };

    const first = await query(true);
    if (!first.error) {
      return (first.data as TeacherLesson[] | null) ?? [];
    }

    const isMissingArchivedColumn =
      typeof first.error.message === 'string' &&
      first.error.message.toLowerCase().includes('archived');
    if (!isMissingArchivedColumn) return [];

    const fallback = await supabase
      .from('lessons')
      .select('id,class_id,title,lesson_date,description,sequence')
      .order('lesson_date', { ascending: true });
    if (fallback.error) return [];
    return (fallback.data as TeacherLesson[] | null) ?? [];
  },

  async saveLesson(payload: Omit<TeacherLesson, 'id'> & { id?: string }) {
    const { data, error } = await supabase.from('lessons').upsert(payload).select().single();
    if (error) return { ok: false, error: error.message };
    return { ok: true, data: data as TeacherLesson };
  },

  async getWork(type: 'quiz' | 'assignment'): Promise<TeacherWorkItem[]> {
    const table = type === 'quiz' ? 'quizzes' : 'assignments';
    const { data, error } = await supabase
      .from(table)
      .select('id,class_id,title,description,due_at,published_at,points,archived')
      .order('due_at', { ascending: true });
    if (error) return [];
    const mapped = (data ?? []).map((row) => ({ ...(row as Omit<TeacherWorkItem, 'type'>), type }));
    return mapped;
  },

  async saveWork(payload: Omit<TeacherWorkItem, 'id'> & { id?: string }) {
    const table = payload.type === 'quiz' ? 'quizzes' : 'assignments';
    const { data, error } = await supabase
      .from(table)
      .upsert({
        id: payload.id,
        class_id: payload.class_id,
        title: payload.title,
        description: payload.description ?? null,
        due_at: payload.due_at,
        points: payload.points,
        archived: payload.archived ?? false,
      })
      .select('id,class_id,title,description,due_at,published_at,points,archived')
      .single();
    if (error) return { ok: false, error: error.message };
    return { ok: true, data: { ...(data as Omit<TeacherWorkItem, 'type'>), type: payload.type } };
  },

  async publishWork(type: 'quiz' | 'assignment', workId: string) {
    const id = workId.trim();
    if (!id) return { ok: false as const, error: 'Work id is required.' };
    const table = type === 'quiz' ? 'quizzes' : 'assignments';
    const publishedAt = new Date().toISOString();
    const { data, error } = await supabase
      .from(table)
      .update({ published_at: publishedAt })
      .eq('id', id)
      .select('id,class_id,title,description,due_at,published_at,points,archived')
      .single();
    if (error) return { ok: false as const, error: error.message };
    return { ok: true as const, data: { ...(data as Omit<TeacherWorkItem, 'type'>), type } };
  },

  async getWorkById(type: 'quiz' | 'assignment', workId: string): Promise<TeacherWorkItem | null> {
    const id = workId.trim();
    if (!id) return null;
    const table = type === 'quiz' ? 'quizzes' : 'assignments';
    const { data, error } = await supabase
      .from(table)
      .select('id,class_id,title,description,due_at,published_at,points,archived')
      .eq('id', id)
      .single();
    if (error) return null;
    return { ...(data as Omit<TeacherWorkItem, 'type'>), type };
  },

  async getSubmissionsForWork(
    workType: 'quiz' | 'assignment',
    workId: string,
    options?: { classId?: string; workTitle?: string }
  ): Promise<TeacherGradeRow[]> {
    const id = workId.trim();
    if (!id) return [];

    const byIdRes = await supabase
      .from('submissions')
      .select('*')
      .eq('work_type', workType)
      .eq('work_id', id)
      .order('submitted_at', { ascending: false })
      .limit(2000);

    const classId = options?.classId?.trim();
    const workTitle = options?.workTitle?.trim();
    const fallbackRes =
      classId && workTitle
        ? await supabase
            .from('submissions')
            .select('*')
            .eq('work_type', workType)
            .eq('class_id', classId)
            .eq('work_title', workTitle)
            .order('submitted_at', { ascending: false })
            .limit(2000)
        : null;

    const mappedById = byIdRes.error ? [] : mapSubmissionRows((((byIdRes.data ?? []) as unknown[]) as LooseRow[]));
    const mappedFallback =
      !fallbackRes || fallbackRes.error ? [] : mapSubmissionRows((((fallbackRes.data ?? []) as unknown[]) as LooseRow[]));

    if (mappedById.length === 0 && mappedFallback.length === 0) return [];
    return dedupeSubmissionRows([...mappedById, ...mappedFallback]);
  },

  async getGrades(): Promise<TeacherGradeRow[]> {
    const { data, error } = await supabase.from('submissions').select('*').order('submitted_at', { ascending: false }).limit(2000);
    if (error) return [];
    const mapped = mapSubmissionRows((((data ?? []) as unknown[]) as LooseRow[]));
    return enrichRowsWithStudentProfiles(mapped);
  },

  async getGradeSubmissionDetail(submissionId: string): Promise<TeacherGradeSubmissionDetail | null> {
    const id = submissionId.trim();
    if (!id) return null;

    const { data, error } = await supabase.from('submissions').select('*').eq('id', id).maybeSingle();
    if (error || !data) return null;

    const row = ((data as unknown) as LooseRow);
    const mapped = mapSubmissionRows([row])[0];
    const [enriched] = await enrichRowsWithStudentProfiles([mapped]);
    return {
      submission: enriched ?? mapped,
      answers_by_question_id: pickAnswerMap(row),
    };
  },

  async getActivitySummary(): Promise<TeacherActivitySummary> {
    const [lessonsRes, enrollmentsRes, lessonCompletionsRes, submissionsRes] = await Promise.all([
      supabase.from('lessons').select('id,class_id').limit(5000),
      supabase.from('class_enrollments').select('class_id,student_id').limit(5000),
      supabase.from('lesson_completions').select('lesson_id,class_id,student_id').limit(10000),
      supabase.from('submissions').select('status,work_type').limit(5000),
    ]);

    if (lessonsRes.error || enrollmentsRes.error || lessonCompletionsRes.error || submissionsRes.error) {
      return {
        lessonsCompleted: 0,
        lessonsMissed: 0,
        assignmentsCompleted: 0,
        assignmentsMissed: 0,
        totalLessons: 0,
        totalExpectedLessonCompletions: 0,
      };
    }

    const lessonsByClass = new Map<string, number>();
    (((lessonsRes.data ?? []) as unknown[]) as LooseRow[]).forEach((row) => {
      const classId = pickString(row, ['class_id', 'classId']);
      if (!classId) return;
      lessonsByClass.set(classId, (lessonsByClass.get(classId) ?? 0) + 1);
    });

    const enrollmentSetByClass = new Map<string, Set<string>>();
    (((enrollmentsRes.data ?? []) as unknown[]) as LooseRow[]).forEach((row) => {
      const classId = pickString(row, ['class_id', 'classId']);
      const studentId = pickString(row, ['student_id', 'studentId']);
      if (!classId || !studentId) return;
      const set = enrollmentSetByClass.get(classId) ?? new Set<string>();
      set.add(studentId);
      enrollmentSetByClass.set(classId, set);
    });

    let totalExpectedLessonCompletions = 0;
    lessonsByClass.forEach((lessonCount, classId) => {
      const enrolled = enrollmentSetByClass.get(classId)?.size ?? 0;
      totalExpectedLessonCompletions += lessonCount * enrolled;
    });

    const completedLessonPairs = new Set<string>();
    (((lessonCompletionsRes.data ?? []) as unknown[]) as LooseRow[]).forEach((row) => {
      const lessonId = pickString(row, ['lesson_id', 'lessonId']);
      const studentId = pickString(row, ['student_id', 'studentId']);
      if (!lessonId || !studentId) return;
      completedLessonPairs.add(`${lessonId}:${studentId}`);
    });
    const lessonsCompleted = completedLessonPairs.size;
    const lessonsMissed = Math.max(0, totalExpectedLessonCompletions - lessonsCompleted);

    let assignmentsCompleted = 0;
    let assignmentsMissed = 0;
    (((submissionsRes.data ?? []) as unknown[]) as LooseRow[]).forEach((row) => {
      const workType = (pickString(row, ['work_type', 'type']) ?? 'assignment').toLowerCase();
      if (workType !== 'assignment' && workType !== 'quiz') return;
      const status = (pickString(row, ['status']) ?? 'submitted').toLowerCase();
      if (status === 'graded' || status === 'returned') assignmentsCompleted += 1;
      if (status === 'missing') assignmentsMissed += 1;
    });

    return {
      lessonsCompleted,
      lessonsMissed,
      assignmentsCompleted,
      assignmentsMissed,
      totalLessons: (lessonsRes.data ?? []).length,
      totalExpectedLessonCompletions,
    };
  },

  async createClass(payload: { name: string; description?: string | null; studentIds?: string[]; classImageUrl?: string | null }) {
    const className = payload.name.trim();
    if (!className) {
      return { ok: false as const, error: 'Class name is required.' };
    }

    const { data: authData } = await supabase.auth.getUser();
    const createdBy = authData.user?.id ?? null;

    const insertPayload: Record<string, unknown> = { name: className };
    if (createdBy) {
      insertPayload.created_by = createdBy;
    }
    if (typeof payload.description === 'string' && payload.description.trim()) {
      insertPayload.description = payload.description.trim();
    }
    if (typeof payload.classImageUrl === 'string' && payload.classImageUrl.trim()) {
      insertPayload.class_image_url = payload.classImageUrl.trim();
    }

    const { data, error } = await supabase.from('classes').insert(insertPayload).select('id,name').single();
    if (error) return { ok: false as const, error: error.message };

    const studentIds = Array.from(new Set(payload.studentIds ?? [])).filter(Boolean);
    if (studentIds.length > 0) {
      const enrollmentRows = studentIds.map((studentId) => ({
        class_id: (data as { id: string }).id,
        student_id: studentId,
      }));
      const { error: enrollmentError } = await supabase.from('class_enrollments').insert(enrollmentRows);
      if (enrollmentError) {
        return { ok: false as const, error: enrollmentError.message };
      }
    }

    return {
      ok: true as const,
      data: data as { id: string; name: string },
    };
  },

  async enrollStudents(payload: { classId: string; studentIds: string[] }) {
    const classIdSafe = payload.classId.trim();
    if (!classIdSafe) {
      return { ok: false as const, error: 'Class id is required.' };
    }

    const studentIds = Array.from(new Set(payload.studentIds.map((id) => id.trim()).filter(Boolean)));
    if (studentIds.length === 0) {
      return { ok: false as const, error: 'Select at least one student.' };
    }

    const enrollmentRows = studentIds.map((studentId) => ({
      class_id: classIdSafe,
      student_id: studentId,
    }));
    const { error } = await supabase.from('class_enrollments').upsert(enrollmentRows, {
      onConflict: 'class_id,student_id',
      ignoreDuplicates: true,
    });
    if (error) return { ok: false as const, error: error.message };

    return { ok: true as const, addedCount: studentIds.length };
  },

  async unenrollStudents(payload: { classId: string; studentIds: string[] }) {
    const classIdSafe = payload.classId.trim();
    if (!classIdSafe) {
      return { ok: false as const, error: 'Class id is required.' };
    }

    const studentIds = Array.from(new Set(payload.studentIds.map((id) => id.trim()).filter(Boolean)));
    if (studentIds.length === 0) {
      return { ok: false as const, error: 'Select at least one student.' };
    }

    const { data, error } = await supabase
      .from('class_enrollments')
      .delete()
      .eq('class_id', classIdSafe)
      .in('student_id', studentIds)
      .select('id');
    if (error) return { ok: false as const, error: error.message };

    const removedCount = (data ?? []).length;
    if (removedCount === 0) {
      return { ok: false as const, error: 'Unable to remove student from this class.' };
    }

    return { ok: true as const, removedCount };
  },

  async getClassById(classId: string): Promise<TeacherClassDetails | null> {
    const classIdSafe = classId.trim();
    if (!classIdSafe) return null;
    const { data, error } = await supabase
      .from('classes')
      .select('id,name,description,class_image_url')
      .eq('id', classIdSafe)
      .single();
    if (error) return null;
    return (data as TeacherClassDetails | null) ?? null;
  },

  async getClassTextbooks(classId: string): Promise<TeacherClassTextbook[]> {
    const classIdSafe = classId.trim();
    if (!classIdSafe) return [];
    const { data, error } = await supabase
      .from('class_textbooks')
      .select('id,class_id,title,file_path,file_url,page_count,uploaded_by,created_at,updated_at')
      .eq('class_id', classIdSafe)
      .order('created_at', { ascending: false });
    if (error) return [];
    return ((data as TeacherClassTextbook[] | null) ?? []).map((row) => ({
      ...row,
      page_count: Number.isFinite(row.page_count) ? Math.max(1, Math.floor(row.page_count)) : 1,
    }));
  },

  async getClassSyllabus(classId: string): Promise<TeacherClassSyllabus | null> {
    const classIdSafe = classId.trim();
    if (!classIdSafe) return null;
    const { data, error } = await supabase
      .from('class_syllabi')
      .select('id,class_id,title,file_path,file_url,uploaded_by,created_at,updated_at')
      .eq('class_id', classIdSafe)
      .maybeSingle();
    if (error) return null;
    return (data as TeacherClassSyllabus | null) ?? null;
  },

  async upsertClassSyllabus(payload: {
    classId: string;
    title: string;
    filePath: string;
    fileUrl: string;
    uploadedBy?: string | null;
  }) {
    const classIdSafe = payload.classId.trim();
    const title = payload.title.trim();
    const filePath = payload.filePath.trim();
    const fileUrl = payload.fileUrl.trim();
    if (!classIdSafe) return { ok: false as const, error: 'Class id is required.' };
    if (!title) return { ok: false as const, error: 'Syllabus title is required.' };
    if (!filePath || !fileUrl) return { ok: false as const, error: 'Syllabus file URL is required.' };

    const { data, error } = await supabase
      .from('class_syllabi')
      .upsert(
        {
          class_id: classIdSafe,
          title,
          file_path: filePath,
          file_url: fileUrl,
          uploaded_by: payload.uploadedBy ?? null,
        },
        { onConflict: 'class_id' }
      )
      .select('id,class_id,title,file_path,file_url,uploaded_by,created_at,updated_at')
      .single();
    if (error) return { ok: false as const, error: error.message };
    return { ok: true as const, data: data as TeacherClassSyllabus };
  },

  async deleteClassSyllabus(classId: string) {
    const classIdSafe = classId.trim();
    if (!classIdSafe) return { ok: false as const, error: 'Class id is required.' };
    const { data, error } = await supabase
      .from('class_syllabi')
      .delete()
      .eq('class_id', classIdSafe)
      .select('id,class_id,title,file_path,file_url,uploaded_by,created_at,updated_at')
      .maybeSingle();
    if (error) return { ok: false as const, error: error.message };
    return { ok: true as const, data: (data as TeacherClassSyllabus | null) ?? null };
  },

  async createClassTextbook(payload: {
    classId: string;
    title: string;
    filePath: string;
    fileUrl: string;
    pageCount: number;
    uploadedBy?: string | null;
  }) {
    const classIdSafe = payload.classId.trim();
    const title = payload.title.trim();
    const filePath = payload.filePath.trim();
    const fileUrl = payload.fileUrl.trim();
    const pageCount = Math.floor(payload.pageCount);
    if (!classIdSafe) return { ok: false as const, error: 'Class id is required.' };
    if (!title) return { ok: false as const, error: 'Textbook title is required.' };
    if (!filePath || !fileUrl) return { ok: false as const, error: 'Textbook file URL is required.' };
    if (!Number.isFinite(pageCount) || pageCount <= 0) return { ok: false as const, error: 'Textbook page count is required.' };

    const { data, error } = await supabase
      .from('class_textbooks')
      .insert({
        class_id: classIdSafe,
        title,
        file_path: filePath,
        file_url: fileUrl,
        page_count: pageCount,
        uploaded_by: payload.uploadedBy ?? null,
      })
      .select('id,class_id,title,file_path,file_url,page_count,uploaded_by,created_at,updated_at')
      .single();
    if (error) return { ok: false as const, error: error.message };
    return { ok: true as const, data: data as TeacherClassTextbook };
  },

  async deleteClassTextbook(textbookId: string) {
    const textbookIdSafe = textbookId.trim();
    if (!textbookIdSafe) return { ok: false as const, error: 'Textbook id is required.' };
    const { data, error } = await supabase
      .from('class_textbooks')
      .delete()
      .eq('id', textbookIdSafe)
      .select('id,class_id,title,file_path,file_url,page_count,uploaded_by,created_at,updated_at')
      .single();
    if (error) return { ok: false as const, error: error.message };
    return { ok: true as const, data: data as TeacherClassTextbook };
  },

  async updateClass(payload: { id: string; name: string; description?: string | null; classImageUrl?: string | null }) {
    const classIdSafe = payload.id.trim();
    const className = payload.name.trim();
    if (!classIdSafe) {
      return { ok: false as const, error: 'Class id is required.' };
    }
    if (!className) {
      return { ok: false as const, error: 'Class name is required.' };
    }

    const updatePayload: Record<string, unknown> = {
      name: className,
      description: typeof payload.description === 'string' && payload.description.trim() ? payload.description.trim() : null,
    };
    if (payload.classImageUrl !== undefined) {
      updatePayload.class_image_url =
        typeof payload.classImageUrl === 'string' && payload.classImageUrl.trim() ? payload.classImageUrl.trim() : null;
    }

    const { data, error } = await supabase
      .from('classes')
      .update(updatePayload)
      .eq('id', classIdSafe)
      .select('id,name,description,class_image_url')
      .single();

    if (error) return { ok: false as const, error: error.message };
    return { ok: true as const, data: data as TeacherClassDetails };
  },

  async deleteClass(classId: string) {
    const classIdSafe = classId.trim();
    if (!classIdSafe) {
      return { ok: false as const, error: 'Class id is required.' };
    }

    const { error } = await supabase.from('classes').delete().eq('id', classIdSafe);
    if (error) return { ok: false as const, error: error.message };
    return { ok: true as const };
  },

  async getStudentOptions(): Promise<ClassroomStudentOption[]> {
    const DIRECTORY_PAGE_SIZE = 1000;
    const usersRows: Array<{ id?: string | null; full_name?: string | null; email?: string | null }> = [];
    const kidsRows: Array<{
      id?: string | null;
      user_id?: string | null;
      first_name?: string | null;
      last_name?: string | null;
    }> = [];

    for (let from = 0; ; from += DIRECTORY_PAGE_SIZE) {
      const to = from + DIRECTORY_PAGE_SIZE - 1;
      const { data, error } = await supabase
        .from('users')
        .select('id,full_name,email')
        .overlaps('roles', ['student'])
        .order('full_name', { ascending: true })
        .range(from, to);
      if (error || !data) break;
      const batch = (data as unknown[]) as Array<{
        id?: string | null;
        full_name?: string | null;
        email?: string | null;
      }>;
      usersRows.push(...batch);
      if (batch.length < DIRECTORY_PAGE_SIZE) break;
    }

    for (let from = 0; ; from += DIRECTORY_PAGE_SIZE) {
      const to = from + DIRECTORY_PAGE_SIZE - 1;
      const { data, error } = await supabase
        .from('kids_church_children')
        .select('id,user_id,first_name,last_name,parent_id')
        .not('user_id', 'is', null)
        .order('first_name', { ascending: true })
        .range(from, to);
      if (error || !data) break;
      const batch = (data as unknown[]) as Array<{
        id?: string | null;
        user_id?: string | null;
        first_name?: string | null;
        last_name?: string | null;
      }>;
      kidsRows.push(...batch);
      if (batch.length < DIRECTORY_PAGE_SIZE) break;
    }

    const optionsById = new Map<string, ClassroomStudentOption>();

    usersRows.forEach((row) => {
      const id = String(row.id ?? '').trim();
      if (!id) return;
      optionsById.set(id, {
        id,
        full_name: String(row.full_name ?? '').trim() || 'Unnamed student',
        email: typeof row.email === 'string' ? row.email : null,
      });
    });

    kidsRows.forEach((kid) => {
      const userId = String(kid.user_id ?? '').trim();
      if (!userId) return;
      const name = `${String(kid.first_name ?? '').trim()} ${String(kid.last_name ?? '').trim()}`.trim() || 'Unnamed student';
      const existing = optionsById.get(userId);
      if (existing) return;
      optionsById.set(userId, {
        id: userId,
        full_name: name,
        email: null,
      });
    });

    return Array.from(optionsById.values()).sort((a, b) => a.full_name.localeCompare(b.full_name));
  },

  async getClassStudents(classId: string): Promise<ClassroomStudentOption[]> {
    const classIdSafe = classId.trim();
    if (!classIdSafe) return [];

    const { data: enrollments, error: enrollmentsError } = await supabase
      .from('class_enrollments')
      .select('student_id')
      .eq('class_id', classIdSafe)
      .limit(5000);

    if (enrollmentsError) return [];

    const seen = new Set<string>();
    const studentIds: string[] = [];

    (((enrollments ?? []) as unknown[]) as LooseRow[]).forEach((row) => {
      const studentId = pickString(row, ['student_id', 'studentId']);
      if (!studentId || seen.has(studentId)) return;
      seen.add(studentId);
      studentIds.push(studentId);
    });

    if (!studentIds.length) return [];

    const userById = new Map<string, ClassroomStudentOption>();
    const childNameByUserId = new Map<string, string>();

    const chunkSize = 100;
    for (let i = 0; i < studentIds.length; i += chunkSize) {
      const slice = studentIds.slice(i, i + chunkSize);
      const [{ data: userRows }, { data: kidRows }] = await Promise.all([
        supabase.from('users').select('id, full_name, email, avatar').in('id', slice),
        supabase
          .from('kids_church_children')
          .select('user_id, first_name, last_name')
          .in('user_id', slice),
      ]);

      (((userRows ?? []) as unknown[]) as LooseRow[]).forEach((row) => {
        const userId = pickString(row, ['id']);
        if (!userId) return;
        userById.set(userId, {
          id: userId,
          full_name: pickString(row, ['full_name']) ?? 'Unnamed student',
          email: pickString(row, ['email']),
          avatar_url: pickString(row, ['avatar', 'avatar_url', 'photo_url']),
        });
      });

      (((kidRows ?? []) as unknown[]) as LooseRow[]).forEach((row) => {
        const userId = pickString(row, ['user_id']);
        if (!userId || childNameByUserId.has(userId)) return;
        const childName = `${pickString(row, ['first_name']) ?? ''} ${pickString(row, ['last_name']) ?? ''}`.trim();
        if (childName) {
          childNameByUserId.set(userId, childName);
        }
      });
    }

    const students: ClassroomStudentOption[] = studentIds.map((studentId) => {
      const user = userById.get(studentId);
      if (user) {
        return {
          ...user,
          full_name: user.full_name || childNameByUserId.get(studentId) || 'Unnamed student',
        };
      }

      return {
        id: studentId,
        full_name: childNameByUserId.get(studentId) || 'Unnamed student',
        email: null,
        avatar_url: null,
      };
    });

    students.sort((a, b) => a.full_name.localeCompare(b.full_name));
    return students;
  },

  async saveGrade(payload: Pick<TeacherGradeRow, 'id' | 'score' | 'feedback' | 'status'>) {
    const { error } = await supabase
      .from('submissions')
      .update({
        score: payload.score,
        feedback: payload.feedback,
        status: payload.status,
      })
      .eq('id', payload.id);
    if (error) return { ok: false, error: error.message };
    return { ok: true };
  },

  async getAnnouncements(): Promise<TeacherAnnouncement[]> {
    const { data, error } = await supabase
      .from('announcements')
      .select('id,class_id,title,body,created_at')
      .order('created_at', { ascending: false });
    if (error) return sampleAnnouncements;
    return fallbackOr(data as TeacherAnnouncement[] | null, sampleAnnouncements);
  },

  async createAnnouncement(payload: Omit<TeacherAnnouncement, 'id' | 'created_at'>) {
    const { data, error } = await supabase
      .from('announcements')
      .insert(payload)
      .select('id,class_id,title,body,created_at')
      .single();
    if (error) return { ok: false, error: error.message };
    return { ok: true, data: data as TeacherAnnouncement };
  },

  async getMessages(userId: string): Promise<TeacherMessage[]> {
    const { data, error } = await supabase
      .from('messages')
      .select('id,sender_id,receiver_id,content,created_at')
      .or(`sender_id.eq.${userId},receiver_id.eq.${userId}`)
      .is('hidden_at', null)
      .is('deleted_at', null)
      .order('created_at', { ascending: false });
    if (error) return sampleMessages;
    return fallbackOr(data as TeacherMessage[] | null, sampleMessages);
  },

  async getStudentContacts(): Promise<TeacherMessageContact[]> {
    const options = await this.getStudentOptions();
    return options.map((option) => ({
      id: option.id,
      full_name: option.full_name,
      email: option.email,
      avatar_url: option.avatar_url ?? null,
    }));
  },

  async sendMessage(payload: Pick<TeacherMessage, 'sender_id' | 'receiver_id' | 'content'>) {
    const { data, error } = await supabase
      .from('messages')
      .insert(payload)
      .select('id,sender_id,receiver_id,content,created_at')
      .single();
    if (error) return { ok: false, error: error.message };
    return { ok: true, data: data as TeacherMessage };
  },

  async getTeacherClassesForBroadcast(): Promise<TeacherBroadcastClass[]> {
    const overview = await this.getOverview();
    return overview.map((row) => ({
      id: row.id,
      name: row.name,
      studentCount: row.activeStudents,
    }));
  },

  async sendClassBroadcast(payload: {
    classId: string;
    content: string;
  }): Promise<ClassroomBroadcastResult> {
    const classId = payload.classId.trim();
    const content = payload.content.trim();
    if (!classId) return { ok: false, error: 'Class is required.' };
    if (!content) return { ok: false, error: 'Message is required.' };

    const { data, error } = await supabase.rpc('send_classroom_broadcast_txn', {
      p_class_id: classId,
      p_content: content,
    });

    if (error) return { ok: false, error: error.message };

    const result = (data ?? {}) as {
      ok?: boolean;
      sent_count?: number;
      skipped_count?: number;
      class_id?: string;
      error?: string;
    };

    if (!result.ok) {
      return { ok: false, error: result.error ?? 'Broadcast failed.' };
    }

    return {
      ok: true,
      sent_count: Number(result.sent_count ?? 0),
      skipped_count: Number(result.skipped_count ?? 0),
      class_id: result.class_id ?? classId,
    };
  },

  subscribeToMessages(userId: string, onMessage: (message: TeacherMessage) => void) {
    const channel = supabase
      .channel(`classroom-teacher-messages-${userId}`)
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'messages' },
        (payload) => {
          const message = payload.new as TeacherMessage;
          if (message.sender_id === userId || message.receiver_id === userId) {
            onMessage(message);
          }
        }
      )
      .subscribe();

    return () => {
      void supabase.removeChannel(channel);
    };
  },

  async getDiscussionSettings(classId: string): Promise<TeacherDiscussionSettings> {
    const classIdSafe = classId.trim();
    const fallback: TeacherDiscussionSettings = {
      class_id: classIdSafe,
      is_locked: false,
      updated_by: null,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };
    if (!classIdSafe) return fallback;

    const { data, error } = await supabase
      .from('class_discussion_settings')
      .select('class_id,is_locked,updated_by,created_at,updated_at')
      .eq('class_id', classIdSafe)
      .maybeSingle();
    if (error || !data) return fallback;

    return {
      class_id: data.class_id ?? classIdSafe,
      is_locked: Boolean(data.is_locked),
      updated_by: data.updated_by ?? null,
      created_at: toSafeIso(data.created_at),
      updated_at: toSafeIso(data.updated_at),
    };
  },

  async updateDiscussionSettings(classId: string, isLocked: boolean, updatedBy?: string | null) {
    const classIdSafe = classId.trim();
    if (!classIdSafe) return { ok: false as const, error: 'Class id is required.' };

    const { data, error } = await supabase
      .from('class_discussion_settings')
      .upsert(
        {
          class_id: classIdSafe,
          is_locked: isLocked,
          updated_by: updatedBy ?? null,
        },
        { onConflict: 'class_id' }
      )
      .select('class_id,is_locked,updated_by,created_at,updated_at')
      .single();
    if (error) return { ok: false as const, error: error.message };

    return {
      ok: true as const,
      data: {
        class_id: data.class_id,
        is_locked: Boolean(data.is_locked),
        updated_by: data.updated_by ?? null,
        created_at: toSafeIso(data.created_at),
        updated_at: toSafeIso(data.updated_at),
      } satisfies TeacherDiscussionSettings,
    };
  },

  async getClassDiscussion(classId: string): Promise<TeacherClassDiscussion> {
    const classIdSafe = classId.trim();
    const settings = await this.getDiscussionSettings(classIdSafe);
    if (!classIdSafe) return { settings, posts: [] };

    const { data, error } = await supabase
      .from('class_discussion_posts')
      .select('id,class_id,parent_post_id,author_id,body,status,is_pinned,hidden_at,hidden_by,created_at,updated_at')
      .eq('class_id', classIdSafe)
      .order('is_pinned', { ascending: false })
      .order('created_at', { ascending: false });
    if (error) return { settings, posts: [] };

    const rows = (((data ?? []) as unknown[]) as LooseRow[]);
    const authorIds = Array.from(
      new Set(rows.map((row) => pickString(row, ['author_id', 'authorId']) ?? '').filter(Boolean))
    );
    const authorNameById = new Map<string, string>();

    if (authorIds.length > 0) {
      const { data: usersRows } = await supabase.from('users').select('id,full_name').in('id', authorIds);
      (((usersRows ?? []) as unknown[]) as LooseRow[]).forEach((row) => {
        const id = pickString(row, ['id']);
        if (!id) return;
        authorNameById.set(id, pickString(row, ['full_name']) ?? 'Class member');
      });
    }

    const {
      data: { user },
    } = await supabase.auth.getUser();
    const viewerUserId = user?.id ?? null;
    const postIds = rows.map((row) => pickString(row, ['id']) ?? '').filter(Boolean);
    const reactionsByPostId = await fetchDiscussionReactionSummaries(postIds, viewerUserId);

    return {
      settings,
      posts: mapDiscussionTree(rows, authorNameById, reactionsByPostId),
    };
  },

  async createDiscussionPost(payload: {
    classId: string;
    body: string;
    authorId?: string | null;
  }) {
    const classIdSafe = payload.classId.trim();
    const body = payload.body.trim();
    if (!classIdSafe) return { ok: false as const, error: 'Class id is required.' };
    if (!body) return { ok: false as const, error: 'Post body is required.' };

    const { data, error } = await supabase
      .from('class_discussion_posts')
      .insert({
        class_id: classIdSafe,
        body,
        author_id: payload.authorId ?? null,
      })
      .select('id,class_id,parent_post_id,author_id,body,status,is_pinned,hidden_at,hidden_by,created_at,updated_at')
      .single();
    if (error) return { ok: false as const, error: error.message };
    return { ok: true as const, data };
  },

  async createDiscussionReply(payload: {
    classId: string;
    parentPostId: string;
    body: string;
    authorId?: string | null;
  }) {
    const classIdSafe = payload.classId.trim();
    const parentPostId = payload.parentPostId.trim();
    const body = payload.body.trim();
    if (!classIdSafe) return { ok: false as const, error: 'Class id is required.' };
    if (!parentPostId) return { ok: false as const, error: 'Parent post id is required.' };
    if (!body) return { ok: false as const, error: 'Reply body is required.' };

    const { data, error } = await supabase
      .from('class_discussion_posts')
      .insert({
        class_id: classIdSafe,
        parent_post_id: parentPostId,
        body,
        author_id: payload.authorId ?? null,
      })
      .select('id,class_id,parent_post_id,author_id,body,status,is_pinned,hidden_at,hidden_by,created_at,updated_at')
      .single();
    if (error) return { ok: false as const, error: error.message };
    return { ok: true as const, data };
  },

  async updateDiscussionPost(
    postId: string,
    updates: {
      body?: string;
      status?: DiscussionPostStatus;
      is_pinned?: boolean;
      hidden_at?: string | null;
      hidden_by?: string | null;
    }
  ) {
    const postIdSafe = postId.trim();
    if (!postIdSafe) return { ok: false as const, error: 'Post id is required.' };

    const next: Record<string, unknown> = {};
    if (typeof updates.body === 'string') next.body = updates.body.trim();
    if (updates.status === 'published' || updates.status === 'removed') next.status = updates.status;
    if (typeof updates.is_pinned === 'boolean') next.is_pinned = updates.is_pinned;
    if (updates.hidden_at !== undefined) next.hidden_at = updates.hidden_at;
    if (updates.hidden_by !== undefined) next.hidden_by = updates.hidden_by;
    if (Object.keys(next).length === 0) return { ok: false as const, error: 'No changes provided.' };

    const { data, error } = await supabase
      .from('class_discussion_posts')
      .update(next)
      .eq('id', postIdSafe)
      .select('id,class_id,parent_post_id,author_id,body,status,is_pinned,hidden_at,hidden_by,created_at,updated_at')
      .single();
    if (error) return { ok: false as const, error: error.message };
    return { ok: true as const, data };
  },

  async toggleDiscussionPostReaction(postId: string, emoji: string) {
    const postIdSafe = postId.trim();
    const emojiSafe = emoji.trim();
    if (!postIdSafe) return { ok: false as const, error: 'Post id is required.' };
    if (!emojiSafe) return { ok: false as const, error: 'Emoji is required.' };

    const { data, error } = await supabase.rpc('toggle_class_discussion_post_reaction', {
      p_post_id: postIdSafe,
      p_emoji: emojiSafe,
    });
    if (error) return { ok: false as const, error: error.message };

    const payloadData = (data ?? {}) as {
      action?: string;
      reactions?: DiscussionReactionSummary[];
    };
    const action = typeof payloadData.action === 'string' ? payloadData.action.toLowerCase() : 'updated';
    const reactions = Array.isArray(payloadData.reactions)
      ? payloadData.reactions
          .map((reaction) => ({
            emoji: String(reaction?.emoji ?? '').trim(),
            count: Number(reaction?.count ?? 0) || 0,
            reactedByMe: Boolean(reaction?.reactedByMe),
          }))
          .filter((reaction) => reaction.emoji.length > 0 && reaction.count > 0)
      : [];

    if (action && action !== 'removed') {
      try {
        const {
          data: { user },
        } = await supabase.auth.getUser();
        if (user?.id) {
          const [{ data: postData }, { data: reactorData }] = await Promise.all([
            supabase
              .from('class_discussion_posts')
              .select('author_id, class_id, body')
              .eq('id', postIdSafe)
              .maybeSingle(),
            supabase.from('users').select('full_name').eq('id', user.id).maybeSingle(),
          ]);

          if (postData?.author_id && postData.author_id !== user.id && postData.class_id) {
            const reactorName =
              typeof reactorData?.full_name === 'string' && reactorData.full_name.trim()
                ? reactorData.full_name.trim()
                : 'Someone';
            let className: string | undefined;
            const { data: classData } = await supabase
              .from('classes')
              .select('name')
              .eq('id', postData.class_id)
              .maybeSingle();
            if (typeof classData?.name === 'string' && classData.name.trim()) {
              className = classData.name.trim();
            }

            const message = className
              ? `${reactorName} reacted ${emojiSafe} to your discussion post in ${className}`
              : `${reactorName} reacted ${emojiSafe} to your discussion post`;
            const title = className
              ? `${reactorName} reacted ${emojiSafe} in ${className}`
              : `${reactorName} reacted ${emojiSafe} to your discussion post`;
            const bodyPreview =
              typeof postData.body === 'string' && postData.body.trim()
                ? postData.body.trim().slice(0, 120)
                : message;
            const notificationData = {
              destination: 'classroom_discussion',
              classId: postData.class_id,
              class_id: postData.class_id,
              className: className ?? null,
              class_name: className ?? null,
              postId: postIdSafe,
              post_id: postIdSafe,
              action: 'reaction',
              reaction_emoji: emojiSafe,
              reactionTarget: 'discussion_post',
              senderId: user.id,
              senderName: reactorName,
              type: 'classroom_discussion_reaction',
            };

            await supabase.rpc('create_notification', {
              p_recipient_id: postData.author_id,
              p_sender_id: user.id,
              p_type: 'like',
              p_title: 'New Reaction',
              p_message: message,
              p_data: notificationData,
            });

            await supabase.functions.invoke('send-push-notification', {
              body: {
                title,
                body: bodyPreview,
                category: 'general',
                targetUsers: [postData.author_id],
                data: notificationData,
              },
            });
          }
        }
      } catch (pushError) {
        console.warn('Failed to send discussion reaction push notification:', pushError);
      }
    }

    return {
      ok: true as const,
      data: {
        action: typeof payloadData.action === 'string' ? payloadData.action : 'updated',
        reactions,
      },
    };
  },

  subscribeToClassDiscussion(classId: string, onChange: () => void) {
    const classIdSafe = classId.trim();
    if (!classIdSafe) return () => {};

    const channel = supabase
      .channel(`class-discussion-teacher-${classIdSafe}-${Date.now()}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'class_discussion_posts',
          filter: `class_id=eq.${classIdSafe}`,
        },
        () => onChange()
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'class_discussion_settings',
          filter: `class_id=eq.${classIdSafe}`,
        },
        () => onChange()
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'class_discussion_post_reactions',
          filter: `class_id=eq.${classIdSafe}`,
        },
        () => onChange()
      )
      .subscribe();

    return () => {
      void supabase.removeChannel(channel);
    };
  },

  subscribeToClassWork(classId: string, onChange: () => void) {
    const classIdSafe = classId.trim();
    if (!classIdSafe) return () => {};

    const channel = supabase
      .channel(`class-work-teacher-${classIdSafe}-${Date.now()}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'assignments',
          filter: `class_id=eq.${classIdSafe}`,
        },
        () => onChange()
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'quizzes',
          filter: `class_id=eq.${classIdSafe}`,
        },
        () => onChange()
      )
      .subscribe();

    return () => {
      void supabase.removeChannel(channel);
    };
  },
};
